/**
 * Validación de País procedencia / País destino vs código de operación (FMM / DANE).
 * Clasificación alineada con el front existente: Colombia 169, ZFs con origin_country IZ o is_free_zone.
 */

const COLOMBIA_DANE = '169'

/**
 * Compara códigos DANE entre catálogo de países y zona franca del usuario (evita falsos negativos por ceros a la izquierda o tipos distintos).
 */
export function daneCodesMatch (a, b) {
  const sa = String(a ?? '').trim()
  const sb = String(b ?? '').trim()
  if (!sa || !sb) return false
  if (sa === sb) return true
  const na = Number(sa)
  const nb = Number(sb)
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb
}

/** Fila del catálogo de países (API / select) considerada zona franca para reglas FMM. */
export function catalogRowIsFreeZone (row) {
  if (!row) return false
  if (row.origin_country === 'IZ') return true
  if (Number(row.is_free_zone) === 1) return true
  return false
}

/**
 * Opciones de País destino para formularios de salida (ítems/productos) según código de operación.
 * @returns {Array|null} Lista filtrada, o null para usar el catálogo completo.
 */
export function getExitFormCountryDestinyOptions (infoCountry, codeOperations, zoneDaneCode) {
  const code = Number(codeOperations)
  if (!Number.isFinite(code) || !infoCountry?.length) return null
  if (code >= 401 && code <= 499) {
    return infoCountry.filter(({ code: c }) => String(c).trim() === COLOMBIA_DANE)
  }
  if (code >= 601 && code <= 699) {
    const zf = infoCountry.filter((row) => catalogRowIsFreeZone(row))
    return zf.length > 0 ? zf : null
  }
  if (code >= 801 && code <= 899) {
    if (zoneDaneCode == null || String(zoneDaneCode).trim() === '') return null
    const cur = infoCountry.filter(
      (row) => catalogRowIsFreeZone(row) && daneCodesMatch(row.code, zoneDaneCode)
    )
    return cur.length > 0 ? cur : null
  }
  return null
}

/**
 * Opciones sugeridas de País procedencia para ingresos (500-599 y 700-799).
 * @returns {Array|null}
 */
export function getEntryFormCountryProcedenciaOptions (infoCountry, codeOperations, zoneDaneCode) {
  const code = Number(codeOperations)
  if (!Number.isFinite(code) || !infoCountry?.length) return null
  if (code >= 500 && code <= 599) {
    const zf = infoCountry.filter((row) => catalogRowIsFreeZone(row))
    return zf.length > 0 ? zf : null
  }
  if (code >= 700 && code <= 799) {
    if (zoneDaneCode == null || String(zoneDaneCode).trim() === '') return null
    const cur = infoCountry.filter(
      (row) => catalogRowIsFreeZone(row) && daneCodesMatch(row.code, zoneDaneCode)
    )
    return cur.length > 0 ? cur : null
  }
  return null
}

/**
 * Código de operación efectivo para reglas FMM: prioriza la cabecera; evita "" / [] y `Number('')` → 0;
 * luego el Redux del formulario cargado (p. ej. al editar o tras refrescar).
 */
export function resolveFmmOperationCode (selectItemForm, dataFormRedux) {
  if (selectItemForm != null && selectItemForm !== '' && !Array.isArray(selectItemForm)) {
    const n = Number(selectItemForm)
    if (Number.isFinite(n) && n > 0) {
      return n
    }
  }
  const op = dataFormRedux?.code_operation
  const opC = dataFormRedux?.code_operationCorrected
  if (op !== undefined && op !== null && op !== '' && !Array.isArray(op)) {
    const n = Number(op)
    if (Number.isFinite(n) && n > 0) {
      return n
    }
  }
  if (opC !== undefined && opC !== null && opC !== '' && !Array.isArray(opC)) {
    const n = Number(opC)
    if (Number.isFinite(n) && n > 0) {
      return n
    }
  }
  return Number.NaN
}

function isFreeZoneCountry (option) {
  return catalogRowIsFreeZone(option)
}

function isColombia (option) {
  return String(option?.code ?? '').trim() === COLOMBIA_DANE
}

function isRestOfWorld (option) {
  return !isColombia(option) && !isFreeZoneCountry(option)
}

/**
 * El contexto a veces deja el select como [] tras limpiar; normaliza a un objeto { value, label } o null.
 */
export function normalizeCountrySelectOption (option) {
  if (option == null) return null
  if (Array.isArray(option)) {
    const first = option[0]
    return first && first.value != null ? first : null
  }
  return option
}

/**
 * Completa code / origin_country / is_free_zone desde el catálogo o desde el label "COD Nombre".
 */
export function enrichCountryOption (option, list) {
  if (!option?.value) return null
  const row = list?.find(
    (c) =>
      Number(c.value) === Number(option.value) ||
      (c.id != null && Number(c.id) === Number(option.value))
  )
  if (row) {
    return {
      ...option,
      code: row.code ?? row.code_country,
      origin_country: row.origin_country,
      is_free_zone: row.is_free_zone
    }
  }
  const parts = String(option.label || '').trim().split(/\s+/)
  const codeFromLabel = parts.length ? parts[0] : ''
  return {
    ...option,
    code: option.code ?? codeFromLabel,
    origin_country: option.origin_country,
    is_free_zone: option.is_free_zone
  }
}

/**
 * Ingresos: valida País procedencia según rango del código de operación.
 */
export function validateFmmProcedenciaForOperation (operationCode, countryOption, zoneDaneCode) {
  const code = Number(operationCode)
  if (code === 120) return { ok: true }
  if (Number.isNaN(code)) return { ok: true }

  const c = countryOption
  if (!c?.value) return { ok: true }

  if (code >= 100 && code <= 199) {
    if (code === 114) return { ok: true }
    if (!isRestOfWorld(c)) {
      return {
        ok: false,
        message:
          'Para códigos de operación 100-199 (ingresos del resto del mundo), el País de procedencia debe ser un país del resto del mundo (no Colombia ni zona franca).'
      }
    }
  } else if (code >= 300 && code <= 399) {
    if (!isColombia(c)) {
      return {
        ok: false,
        message:
          'Para códigos de operación 300-399 (ingresos del territorio nacional), el País de procedencia debe ser Colombia.'
      }
    }
  } else if (code >= 500 && code <= 599) {
    if (!zoneDaneCode) {
      return {
        ok: false,
        message:
          'No se pudo determinar el código DANE de su zona franca. Vuelva a iniciar sesión o contacte al administrador.'
      }
    }
    const sameZone = daneCodesMatch(c.code, zoneDaneCode)
    if (!isFreeZoneCountry(c) || sameZone) {
      return {
        ok: false,
        message:
          'Para códigos de operación 500-599 (ingreso desde otra zona franca), el País de procedencia debe ser una zona franca distinta a la suya.'
      }
    }
  } else if (code >= 700 && code <= 799) {
    if (!zoneDaneCode) {
      return {
        ok: false,
        message:
          'No se pudo determinar el código DANE de su zona franca. Vuelva a iniciar sesión o contacte al administrador.'
      }
    }
    const onlyCurrent =
      isFreeZoneCountry(c) && daneCodesMatch(c.code, zoneDaneCode)
    if (!onlyCurrent) {
      return {
        ok: false,
        message:
          'Para códigos de operación 700-799 (ingreso desde la misma zona franca), el País de procedencia debe ser únicamente la zona franca en la que está ubicado.'
      }
    }
  }

  return { ok: true }
}

/**
 * Salidas: valida País destino según rango del código de operación.
 */
export function validateFmmDestinoForOperation (operationCode, countryOption, zoneDaneCode) {
  const code = Number(operationCode)
  if (code === 120) return { ok: true }

  const c = countryOption
  if (!c?.value) return { ok: true }
  if (Number.isNaN(code) || code < 1) {
    return {
      ok: false,
      message:
        'Debe seleccionar el tipo de operación en la cabecera del formulario (código de operación) para validar el país de destino.'
    }
  }

  if (code >= 200 && code <= 299) {
    if (code === 214 || code === 226) return { ok: true }
    if (!isRestOfWorld(c)) {
      return {
        ok: false,
        message:
          'Para códigos de operación 200-299 (salidas al resto del mundo), el País destino debe ser un país del resto del mundo (no Colombia ni zona franca).'
      }
    }
  } else if (code >= 400 && code <= 499) {
    if (!isColombia(c)) {
      return {
        ok: false,
        message:
          'Para códigos de operación 400-499 (salidas al territorio nacional), el País destino debe ser Colombia.'
      }
    }
  } else if (code >= 600 && code <= 699) {
    if (!zoneDaneCode) {
      return {
        ok: false,
        message:
          'No se pudo determinar el código DANE de su zona franca. Vuelva a iniciar sesión o contacte al administrador.'
      }
    }
    const sameZone = daneCodesMatch(c.code, zoneDaneCode)
    if (!isFreeZoneCountry(c) || sameZone) {
      return {
        ok: false,
        message:
          'Para códigos de operación 600-699 (salida a otra zona franca), el País destino debe ser una zona franca distinta a la suya.'
      }
    }
  } else if (code >= 800 && code <= 899) {
    if (!zoneDaneCode) {
      return {
        ok: false,
        message:
          'No se pudo determinar el código DANE de su zona franca. Vuelva a iniciar sesión o contacte al administrador.'
      }
    }
    const onlyCurrent =
      isFreeZoneCountry(c) && daneCodesMatch(c.code, zoneDaneCode)
    if (!onlyCurrent) {
      return {
        ok: false,
        message:
          'Para códigos de operación 800-899 (salida a usuarios de la misma zona franca), el País destino debe ser únicamente la zona franca en la que está ubicado.'
      }
    }
  }

  return { ok: true }
}


